import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ListBulletIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { accountsAPI, type Account } from '@/services/api'
import { useToast } from '@/hooks/useToast'
import { useFormValidation, validationRules } from '@/hooks/useFormValidation'
import ToastContainer from '@/components/ui/ToastContainer'
import FormField from '@/components/ui/FormField'

const AccountsList: React.FC = () => {
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  // Modal de transferência removido - redireciona para página dedicada
  const [showTransactionsModal, setShowTransactionsModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null)
  const [transactions, setTransactions] = useState<any[]>([])
  const [loadingTransactions, setLoadingTransactions] = useState(false)
  const [balanceAlerts, setBalanceAlerts] = useState<any>(null)
  const { toasts, success, error, removeToast} = useToast()

  // Transfer removido - usa página dedicada em /financeiro/transferencias

  // Validation for account forms
  const accountValidationRules = {
    name: validationRules.accountName,
    type: validationRules.required,
    bank: validationRules.bankName,
    balance: validationRules.positiveNumber
  }

  const {
    errors,
    validateForm,
    validateSingleField,
    clearErrors
  } = useFormValidation(accountValidationRules)

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    bank: '',
    balance: '',
    is_primary: false,
    is_active: true
  })

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true)
        const data = await accountsAPI.list()
        setAccounts(data)

        // Buscar alertas de saldo baixo
        try {
          const alerts = await accountsAPI.getLowBalanceAlerts(500)
          setBalanceAlerts(alerts)
        } catch (err) {
          console.warn('Erro ao buscar alertas de saldo:', err)
        }
      } catch (err: any) {
        error('Erro ao carregar contas', err.message || 'Verifique sua conexão e tente novamente')
      } finally {
        setLoading(false)
      }
    }

    fetchAccounts()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getAccountIcon = (type: string) => {
    switch (type) {
      case 'CHECKING':
        return <BanknotesIcon className="w-6 h-6" />
      case 'SAVINGS':
        return <BuildingLibraryIcon className="w-6 h-6" />
      case 'CREDIT_CARD':
        return <CreditCardIcon className="w-6 h-6" />
      case 'INVESTMENT':
        return <ChartBarIcon className="w-6 h-6" />
      default:
        return <BanknotesIcon className="w-6 h-6" />
    }
  }

  const getAccountTypeLabel = (type: string) => {
    switch (type) {
      case 'CHECKING':
        return 'Conta Corrente'
      case 'SAVINGS':
        return 'Poupança'
      case 'CREDIT_CARD':
        return 'Cartão de Crédito'
      case 'INVESTMENT':
        return 'Investimento'
      default:
        return type
    }
  }

  const getAccountColor = (type: string) => {
    switch (type) {
      case 'CHECKING':
        return 'bg-blue-100 text-blue-800'
      case 'SAVINGS':
        return 'bg-green-100 text-green-800'
      case 'CREDIT_CARD':
        return 'bg-purple-100 text-purple-800'
      case 'INVESTMENT':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      bank: '',
      balance: '',
      is_primary: false,
      is_active: true
    })
    clearErrors()
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form before submitting
    if (!validateForm(formData)) {
      error('Erro de validação', 'Por favor, corrija os campos destacados')
      return
    }

    try {
      await accountsAPI.create({
        name: formData.name,
        type: formData.type as 'CHECKING' | 'SAVINGS' | 'CREDIT_CARD' | 'INVESTMENT',
        bank: formData.bank,
        balance: parseFloat(formData.balance),
        is_primary: formData.is_primary,
        is_active: formData.is_active
      })
      resetForm()
      setShowCreateModal(false)
      success('Conta criada com sucesso!', `A conta "${formData.name}" foi adicionada ao seu portfólio`)
      // Refresh data
      const data = await accountsAPI.list()
      setAccounts(data)
    } catch (err: any) {
      error('Erro ao criar conta', err.message || 'Verifique os dados e tente novamente')
    }
  }

  const handleEdit = (account: Account) => {
    setSelectedAccount(account)
    setFormData({
      name: account.name,
      type: account.type,
      bank: account.bank,
      balance: account.balance.toString(),
      is_primary: account.is_primary || false,
      is_active: account.is_active !== false
    })
    clearErrors()
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedAccount) return

    // Validate form before submitting
    if (!validateForm(formData)) {
      error('Erro de validação', 'Por favor, corrija os campos destacados')
      return
    }

    try {
      await accountsAPI.update(selectedAccount.id, {
        name: formData.name,
        type: formData.type as 'CHECKING' | 'SAVINGS' | 'CREDIT_CARD' | 'INVESTMENT',
        bank: formData.bank,
        balance: parseFloat(formData.balance),
        is_primary: formData.is_primary,
        is_active: formData.is_active
      })
      resetForm()
      setShowEditModal(false)
      setSelectedAccount(null)
      success('Conta atualizada!', `As informações da conta "${formData.name}" foram salvas`)
      // Refresh data
      const data = await accountsAPI.list()
      setAccounts(data)
    } catch (err: any) {
      error('Erro ao atualizar conta', err.message || 'Verifique os dados e tente novamente')
    }
  }

  const handleDelete = async (id: string) => {
    const account = accounts.find(acc => acc.id === id)
    if (window.confirm('Tem certeza que deseja excluir esta conta?')) {
      try {
        await accountsAPI.delete(id)
        success('Conta excluída!', account ? `A conta "${account.name}" foi removida` : 'Conta removida com sucesso')
        // Refresh data
        const data = await accountsAPI.list()
        setAccounts(data)
      } catch (err: any) {
        error('Erro ao excluir conta', err.message || 'Não foi possível remover a conta')
      }
    }
  }

  const handleCorrectBalance = async (accountId: number) => {
    const account = accounts.find(acc => acc.id === accountId.toString())
    if (window.confirm(`Deseja recalcular o saldo de "${account?.name}" baseado nas transações? Esta ação irá corrigir divergências.`)) {
      try {
        const result = await accountsAPI.correctBalance(accountId)
        success('Saldo recalculado!', `Saldo anterior: ${formatCurrency(result.old_balance)} → Novo saldo: ${formatCurrency(result.new_balance)}`)
        // Refresh data
        const data = await accountsAPI.list()
        setAccounts(data)
      } catch (err: any) {
        error('Erro ao recalcular saldo', err.message || 'Não foi possível recalcular o saldo')
      }
    }
  }

  // Função handleTransfer removida - transferências agora são feitas em página dedicada

  const handleViewTransactions = async (account: Account) => {
    setSelectedAccount(account)
    setShowTransactionsModal(true)
    setLoadingTransactions(true)
    setTransactions([])

    try {
      const result = await accountsAPI.getTransactions(parseInt(account.id), {
        limit: 100
      })
      setTransactions(result.transactions || [])
    } catch (err: any) {
      error('Erro ao carregar transações', err.response?.data?.detail || err.message)
    } finally {
      setLoadingTransactions(false)
    }
  }

  const totalBalance = accounts.reduce((sum, account) => {
    const balance = parseFloat(account.balance?.toString() || '0') || 0
    return sum + balance
  }, 0)

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contas</h1>
          <p className="text-gray-600">{accounts.length} contas • Saldo total: {formatCurrency(totalBalance)}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/financeiro/transferencias')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <ArrowsRightLeftIcon className="w-5 h-5" />
            Transferências
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="w-5 h-5" />
            Nova Conta
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Saldo Total</p>
              <p className="text-2xl font-bold">{formatCurrency(totalBalance)}</p>
            </div>
            <BanknotesIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Contas Ativas</p>
              <p className="text-2xl font-bold">{accounts.length}</p>
            </div>
            <BuildingLibraryIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Cartões</p>
              <p className="text-2xl font-bold">
                {accounts.filter(acc => acc.type === 'CREDIT_CARD').length}
              </p>
            </div>
            <CreditCardIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Investimentos</p>
              <p className="text-2xl font-bold">
                {formatCurrency(
                  accounts
                    .filter(acc => acc.type === 'INVESTMENT')
                    .reduce((sum, acc) => sum + acc.balance, 0)
                )}
              </p>
            </div>
            <ChartBarIcon className="w-8 h-8 text-yellow-200" />
          </div>
        </div>
      </div>

      {/* Alertas de Saldo */}
      {balanceAlerts && (balanceAlerts.alerts.critical.length > 0 || balanceAlerts.alerts.warning.length > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
            <h3 className="text-lg font-semibold text-gray-900">Alertas de Saldo</h3>
          </div>

          {balanceAlerts.alerts.critical.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-600 mb-2">⚠️ Saldo Negativo ({balanceAlerts.alerts.critical.length})</h4>
              <div className="space-y-2">
                {balanceAlerts.alerts.critical.map((alert: any) => (
                  <div key={alert.account_id} className="flex justify-between items-center bg-red-50 p-3 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900">{alert.account_name}</span>
                      {alert.bank && <span className="text-sm text-gray-500 ml-2">({alert.bank})</span>}
                    </div>
                    <span className="font-semibold text-red-600">{formatCurrency(alert.balance)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {balanceAlerts.alerts.warning.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-orange-600 mb-2">⚠ Saldo Baixo ({balanceAlerts.alerts.warning.length})</h4>
              <div className="space-y-2">
                {balanceAlerts.alerts.warning.map((alert: any) => (
                  <div key={alert.account_id} className="flex justify-between items-center bg-orange-50 p-3 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900">{alert.account_name}</span>
                      {alert.bank && <span className="text-sm text-gray-500 ml-2">({alert.bank})</span>}
                    </div>
                    <span className="font-semibold text-orange-600">{formatCurrency(alert.balance)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map((account) => (
          <div key={account.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getAccountColor(account.type)}`}>
                  {getAccountIcon(account.type)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{account.name}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getAccountColor(account.type)}`}>
                    {getAccountTypeLabel(account.type)}
                  </span>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleViewTransactions(account)}
                  className="text-green-600 hover:text-green-900 p-1"
                  title="Ver transações"
                >
                  <ListBulletIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleCorrectBalance(parseInt(account.id))}
                  className="text-purple-600 hover:text-purple-900 p-1"
                  title="Recalcular saldo baseado em transações"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEdit(account)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  title="Editar conta"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(account.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Excluir conta"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Banco:</span>
                <span className="font-medium">{account.bank || 'Não informado'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Saldo:</span>
                <span className={`font-bold ${(parseFloat(account.balance?.toString() || '0') || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(parseFloat(account.balance?.toString() || '0') || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Criada em:</span>
                <span className="text-sm text-gray-500">
                  {account.created_at ? new Date(account.created_at).toLocaleDateString('pt-BR') : 'Data não disponível'}
                </span>
              </div>

              <button
                onClick={() => {
                  console.log('🔍 Navegando para extrato:', `/accounts/${account.id}/statement`)
                  console.log('🔍 Account ID:', account.id, 'Type:', typeof account.id)
                  navigate(`/accounts/${account.id}/statement`)
                }}
                className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
              >
                <DocumentTextIcon className="w-5 h-5" />
                Ver Extrato Detalhado
              </button>
            </div>
          </div>
        ))}
      </div>

      {accounts.length === 0 && (
        <div className="text-center py-12">
          <BuildingLibraryIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma conta encontrada</h3>
          <p className="text-gray-500 mb-6">Comece adicionando sua primeira conta bancária</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 mx-auto transition-all duration-200"
          >
            <PlusIcon className="w-5 h-5" />
            Adicionar Primeira Conta
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Nova Conta Bancária</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <FormField
                label="Nome da Conta"
                name="name"
                type="text"
                value={formData.name}
                onChange={(value) => {
                  setFormData({...formData, name: value})
                  validateSingleField('name', value)
                }}
                error={errors.name}
                placeholder="Ex: Conta Corrente Banco do Brasil"
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Tipo de Conta"
                  name="type"
                  type="select"
                  value={formData.type}
                  onChange={(value) => {
                    setFormData({...formData, type: value})
                    validateSingleField('type', value)
                  }}
                  error={errors.type}
                  options={[
                    { value: 'CHECKING', label: 'Conta Corrente' },
                    { value: 'SAVINGS', label: 'Poupança' },
                    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
                    { value: 'INVESTMENT', label: 'Investimento' }
                  ]}
                  required
                />
                <FormField
                  label="Banco"
                  name="bank"
                  type="text"
                  value={formData.bank}
                  onChange={(value) => {
                    setFormData({...formData, bank: value})
                    validateSingleField('bank', value)
                  }}
                  error={errors.bank}
                  placeholder="Ex: Banco do Brasil"
                  required
                />
              </div>

              <FormField
                label="Saldo Inicial"
                name="balance"
                type="number"
                value={formData.balance}
                onChange={(value) => {
                  setFormData({...formData, balance: value})
                  validateSingleField('balance', value)
                }}
                error={errors.balance}
                placeholder="0,00"
                step="0.01"
                required
              />

              <div className="flex items-center space-x-4">
                <FormField
                  label="Conta Principal"
                  name="is_primary"
                  type="checkbox"
                  value={formData.is_primary}
                  onChange={(value) => setFormData({...formData, is_primary: value})}
                />
                <FormField
                  label="Conta Ativa"
                  name="is_active"
                  type="checkbox"
                  value={formData.is_active}
                  onChange={(value) => setFormData({...formData, is_active: value})}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Criar Conta
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Editar Conta</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormField
                label="Nome da Conta"
                name="name"
                type="text"
                value={formData.name}
                onChange={(value) => {
                  setFormData({...formData, name: value})
                  validateSingleField('name', value)
                }}
                error={errors.name}
                placeholder="Ex: Conta Corrente Banco do Brasil"
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Tipo de Conta"
                  name="type"
                  type="select"
                  value={formData.type}
                  onChange={(value) => {
                    setFormData({...formData, type: value})
                    validateSingleField('type', value)
                  }}
                  error={errors.type}
                  options={[
                    { value: 'CHECKING', label: 'Conta Corrente' },
                    { value: 'SAVINGS', label: 'Poupança' },
                    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
                    { value: 'INVESTMENT', label: 'Investimento' }
                  ]}
                  required
                />
                <FormField
                  label="Banco"
                  name="bank"
                  type="text"
                  value={formData.bank}
                  onChange={(value) => {
                    setFormData({...formData, bank: value})
                    validateSingleField('bank', value)
                  }}
                  error={errors.bank}
                  placeholder="Ex: Banco do Brasil"
                  required
                />
              </div>

              <FormField
                label="Saldo"
                name="balance"
                type="number"
                value={formData.balance}
                onChange={(value) => {
                  setFormData({...formData, balance: value})
                  validateSingleField('balance', value)
                }}
                error={errors.balance}
                placeholder="0,00"
                step="0.01"
                required
              />

              <div className="flex items-center space-x-4">
                <FormField
                  label="Conta Principal"
                  name="is_primary"
                  type="checkbox"
                  value={formData.is_primary}
                  onChange={(value) => setFormData({...formData, is_primary: value})}
                />
                <FormField
                  label="Conta Ativa"
                  name="is_active"
                  type="checkbox"
                  value={formData.is_active}
                  onChange={(value) => setFormData({...formData, is_active: value})}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Atualizar Conta
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Transferência removido - use a página /financeiro/transferencias */}

      {/* Transactions Modal */}
      {showTransactionsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Transações - {selectedAccount.name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Saldo atual: <span className="font-semibold">{formatCurrency(parseFloat(selectedAccount.balance?.toString() || '0'))}</span>
                </p>
              </div>
              <button
                onClick={() => setShowTransactionsModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12">
                  <ListBulletIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma transação encontrada para esta conta</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((transaction: any) => (
                    <div
                      key={transaction.id}
                      className={`border rounded-lg p-4 ${
                        transaction.is_transfer
                          ? 'bg-blue-50 border-blue-200'
                          : transaction.type === 'INCOME'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div
                            className={`p-2 rounded-lg ${
                              transaction.is_transfer
                                ? 'bg-blue-100 text-blue-600'
                                : transaction.type === 'INCOME'
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            }`}
                          >
                            {transaction.is_transfer ? (
                              <ArrowsRightLeftIcon className="w-5 h-5" />
                            ) : transaction.type === 'INCOME' ? (
                              <ArrowDownIcon className="w-5 h-5" />
                            ) : (
                              <ArrowUpIcon className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{transaction.description || 'Sem descrição'}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              {transaction.date ? formatDate(transaction.date) : 'Data não disponível'}
                            </p>
                            {transaction.category_name && (
                              <span className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                                {transaction.category_name}
                              </span>
                            )}
                            {transaction.notes && (
                              <p className="text-xs text-gray-500 mt-2">{transaction.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-lg font-semibold ${
                              transaction.type === 'INCOME' ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {transaction.type === 'INCOME' ? '+' : '-'} {formatCurrency(parseFloat(transaction.amount))}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Saldo: {formatCurrency(parseFloat(transaction.balance_after))}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Total de {transactions.length} transaç{transactions.length === 1 ? 'ão' : 'ões'}
              </p>
              <button
                onClick={() => setShowTransactionsModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}

export default AccountsList